/*
 * You came here wondering why this composable even exists? Hear my tl;dr:
 *  - We had leaked server side state via incorrect `set-cookie` cart/token headers
 *  - As a work-around for this DSVGO disaster, Apache rules to drop the relevant headers have been added
 *  - This causes clients to miss cookie deletion for users with invalid auth tokens
 */

export const filterUnique = (value: any, index: number, array: Array<string>) => array.indexOf(value) === index;

/**
 * Delete cookies with workaround for our `set-cookie` header filter.
 *
 * @param cookies Cookies to delete.
 *
 * @usage This function CAN be called without restrictions.
 */
export const useDeleteCookies = (cookies?: Array<string>) => {
  if (!cookies) {
    return;
  }

  const mainStore = useMainStore();
  const event = import.meta.server ? useRequestEvent(useNuxtApp()) : undefined;

  // Fully compatible with our `set-cookie` workaround logic
  for (const cookie of cookies) {
    useCookie(cookie).value = null;

    if (import.meta.server) {
      updateServerCookie(event, cookie, null);
    }
  }

  // This only has to happen during server side rendering since `useCookie()` functionality
  // from server to client is bypassed, but still works correctly *inside* client side rendering
  if (import.meta.server) {
    mainStore.shouldClearCookies = (
      mainStore.shouldClearCookies ? [...cookies, ...mainStore.shouldClearCookies] : cookies
    ).filter(filterUnique);
  }
};

/**
 * Delete client-side cookies which have already been deleted during server-side rendering.
 *
 * Grabs the array `mainStore.shouldClearCookies` which gets populated during server-side rendering
 * and deletes these cookies as a workaround for our apache `set-cookie` header filter.
 *
 * Note: Server-side cookies are correctly deleted when setting `useCookie().value` to `null`. However, we noticed that
 * the returned ref should be reused. Repeated `useCookie()` calls for same cookie miss previous value changes.
 * Client-side completely misses the fact that these cookies were deleted, since we currently drop privacy-related
 * `set-cookie` headers due to a bug with leaked server-side state (token set-cookie header).
 *
 * @usage This function MUST be called once, and SHOULD be called as early as possible.
 */
export const applyDeleteCookies = () => {
  if (import.meta.server) {
    return;
  }

  const { $pinia } = useNuxtApp();
  const mainStore = useMainStore($pinia);

  // Nothing to do
  if (!mainStore.shouldClearCookies) {
    return;
  }

  for (const cookie of mainStore.shouldClearCookies) {
    // Properly deletes cookie for CSR
    useCookie(cookie).value = null;
  }

  // Reset to prevent cookies from being deleted repeatedly
  mainStore.shouldClearCookies = undefined;
};
